import { duration, prefersReducedMotion } from "@/shared/lib";

import { haptic, hapticNotification, hapticSelection, type HapticStyle } from "./sdk";

/**
 * Тактильный слой (FE-14).
 *
 * Устроен по образцу слоя движения: приложение не выбирает силу удара, оно
 * называет НАМЕРЕНИЕ, а сопоставление намерения с вызовом Telegram живёт в
 * одном месте. Без этого шкала расползается за две недели ровно так же, как
 * расползлась бы палитра без правила про литеральные hex: на одном экране
 * `light`, на соседнем `medium`, и приложение перестаёт ощущаться одним
 * предметом.
 *
 * Наружу из `shared/telegram` уходит только `tactile` и `tactileSequence`;
 * три примитива из `sdk.ts` остаются внутренними (правило проверяется тестом
 * «тактильные примитивы» в src/test/quality/rules.test.ts).
 */
export type TactileIntent =
  /** Обычное нажатие: чип, иконка, вспомогательная кнопка. */
  | "tap"
  /** Уход на другой экран: строка списка, пункт навигации, ссылка. */
  | "nav"
  /** Основное действие экрана — CTA `primary`. */
  | "press"
  /** Необратимое действие: купить, вывести, остановить робота. */
  | "commit"
  /** Смена выбранного значения: вкладка, период, тумблер, чекбокс. */
  | "select"
  /** Появление и уход оверлея: шит, модалка. */
  | "surface"
  /**
   * Знак результата, а не исход операции: робот в плюсе / робот в просадке.
   * Ничего не «получилось» и не «сломалось» — просто цифра, которую пальцу
   * дают почувствовать раньше, чем глаз дочитает её цвет.
   */
  | "gain"
  | "drawdown"
  /** Исход операции. */
  | "success"
  | "error"
  | "warning";

/**
 * Удар пальца о поверхность. Разница между ступенями невелика по силе, но
 * именно она отличает «переключил» от «купил».
 */
const IMPACTS: Partial<Record<TactileIntent, HapticStyle>> = {
  tap: "light",
  nav: "soft",
  press: "medium",
  commit: "heavy",
  surface: "rigid",
  // Прибыль мягкая, просадка жёсткая — та же пара, что `soft`/`rigid` у
  // поверхностей, но на другом поводе.
  gain: "soft",
  drawdown: "rigid",
};

/** Исходы: ответ системы, а не касание. Эти три не схлопываются никогда. */
const OUTCOMES = new Set<TactileIntent>(["success", "error", "warning"]);

/** Отклик без всякой фильтрации. Внутренняя функция — см. `tactile` ниже. */
function emit(intent: TactileIntent): void {
  if (intent === "success" || intent === "error" || intent === "warning") {
    hapticNotification(intent);
    return;
  }
  if (intent === "select") {
    hapticSelection();
    return;
  }
  const style = IMPACTS[intent];
  if (style) haptic(style);
}

/**
 * Момент последнего касательного отклика — общий для всех намерений, а не
 * свой у каждого.
 *
 * Так и надо: моторчик не отыгрывает два удара с промежутком в 40 мс
 * раздельно, они сливаются в смазанное гудение. А поводов для такой пары в
 * приложении хватает — нажатие на кнопку и следом появление шита, вложенные
 * интерактивные элементы под одним пальцем, повторное касание. Побеждает
 * первый: он совпадает с моментом, когда палец коснулся стекла.
 */
let lastFired: number | undefined;

/**
 * Отклик по намерению.
 *
 * Ничего не возвращает и никогда не бросает: вне Telegram и на клиентах без
 * поддержки нужного семейства вызов молча ничего не делает — за это отвечает
 * `attempt()` в `sdk.ts`.
 */
export function tactile(intent: TactileIntent): void {
  // Исход операции приходит от сервера, а не от пальца, и совпасть с
  // нажатием может только случайно. Гасить его нельзя: это единственный
  // отклик, который несёт смысл, а не подтверждает касание.
  if (OUTCOMES.has(intent)) {
    emit(intent);
    return;
  }

  // `performance.now()` монотонен: системные часы могут прыгнуть назад, и
  // тогда окно схлопывания замолчало бы до конца сессии.
  const now = performance.now();
  // Окно равно длительности нажатия из слоя движения — той самой, за которую
  // кнопка успевает сжаться и отпуститься.
  if (lastFired !== undefined && now - lastFired < duration("instant")) return;

  lastFired = now;
  emit(intent);
}

/** Шаг хореографии: пауза от начала последовательности и намерение. */
export interface TactileStep {
  /** Задержка в миллисекундах — берётся из `duration()`, не из головы. */
  at: number;
  intent: TactileIntent;
}

/**
 * Хореография из нескольких откликов — тактильная фанфара на экране успеха.
 *
 * Схлопывание повторов здесь намеренно обойдено: ритм последовательности и
 * есть её смысл, а расстояния между шагами заданы автором, а не случайным
 * стечением событий.
 *
 * При системной настройке «уменьшить движение» вырождается в один последний
 * шаг, а не в тишину: анимацию пользователь просил убрать, а подтверждение
 * операции он обязан получить в любом случае.
 *
 * @returns функция отмены — незавершённые шаги снимаются, если экран ушёл
 * раньше, чем доиграла последовательность.
 */
export function tactileSequence(steps: readonly TactileStep[]): VoidFunction {
  if (steps.length === 0) return () => {};

  if (prefersReducedMotion()) {
    const last = steps.reduce((a, b) => (b.at >= a.at ? b : a));
    emit(last.intent);
    return () => {};
  }

  const timers: number[] = [];

  for (const step of steps) {
    // Нулевой шаг играется сразу: таймер на 0 мс отложил бы его до
    // следующего тика и разошёлся бы с кадром, к которому он приклеен.
    if (step.at <= 0) {
      emit(step.intent);
      continue;
    }
    timers.push(
      window.setTimeout(() => {
        emit(step.intent);
      }, step.at),
    );
  }

  // Последовательность считается «последним касанием» целиком: иначе
  // делегат отозвался бы на нажатие посреди фанфары и сбил её ритм.
  lastFired = performance.now();

  return () => {
    for (const timer of timers) window.clearTimeout(timer);
  };
}
